import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const API = '/api/v1/fsm/reports';

const SearchableSelect = ({ options, value, onChange, placeholder, isRTL, inputStyle }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = (options || []).filter(opt =>
    !search || (opt.label || '').toLowerCase().includes(search.toLowerCase())
  );
  const selectedLabel = value ? (options.find(o => String(o.value) === String(value))?.label || value) : '';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen(!open)}
        style={{ ...inputStyle, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 38 }}>
        <span style={{ color: value ? '#e0e0e0' : '#6b7280', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selectedLabel || placeholder}
        </span>
        <span style={{ color: '#6b7280', fontSize: 10, marginInlineStart: 8 }}>▼</span>
      </div>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
          background: '#1a2038', border: '2px solid rgba(99,102,241,0.4)', borderRadius: 8,
          marginTop: 4, maxHeight: 280, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
          <div style={{ padding: 8, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder={isRTL ? '🔍 بحث...' : '🔍 Search...'}
              style={{ ...inputStyle, border: '1px solid rgba(255,255,255,0.15)', padding: '6px 10px', fontSize: 12 }} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            <div onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: !value ? '#6366f1' : '#9ca3af',
                background: !value ? 'rgba(99,102,241,0.1)' : 'transparent', fontWeight: !value ? 700 : 400 }}>
              {placeholder}
            </div>
            {filtered.map(opt => (
              <div key={opt.value} onClick={() => { onChange(String(opt.value)); setOpen(false); setSearch(''); }}
                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12,
                  color: String(value) === String(opt.value) ? '#6366f1' : '#e0e0e0',
                  background: String(value) === String(opt.value) ? 'rgba(99,102,241,0.1)' : 'transparent',
                  fontWeight: String(value) === String(opt.value) ? 700 : 400 }}>
                {opt.label}
              </div>
            ))}
            {filtered.length === 0 && (
              <div style={{ padding: '12px', textAlign: 'center', color: '#4b5563', fontSize: 12 }}>
                {isRTL ? 'لا توجد نتائج' : 'No results'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
const CHART_COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#f97316','#ec4899'];

const FilterControl = ({ filter, filters, onChange, isRTL, inputStyle }) => {
  const label = isRTL && filter.label_ar ? filter.label_ar : (filter.label || filter.field);
  const presetKey = `${filter.field}_preset`;
  if (filter.controlType === 'date_preset') {
    return (<div><label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{label}</label>
      <select value={filters[presetKey] || 30} onChange={e => onChange(presetKey, parseInt(e.target.value))} style={{ ...inputStyle, cursor: 'pointer' }}>
        {[7,14,30,60,90,180,365].map(n => <option key={n} value={n}>{isRTL ? `آخر ${n} يوم` : `Last ${n} days`}</option>)}
      </select></div>);
  }
  if (filter.controlType === 'select') {
    return (<div><label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{label}</label>
      <select value={filters[filter.field] || ''} onChange={e => onChange(filter.field, e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
        <option value="">{isRTL ? 'الكل' : 'All'}</option>
        {(filter.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select></div>);
  }
  if (filter.controlType === 'multi_select') {
    const selected = Array.isArray(filters[filter.field]) ? filters[filter.field] : (filter.value || []);
    return (<div><label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {(filter.options || []).map(opt => {
          const active = selected.includes(opt);
          return (<button key={opt} type="button" onClick={() => onChange(filter.field, active ? selected.filter(s => s !== opt) : [...selected, opt])}
            style={{ padding: '3px 10px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
              background: active ? '#6366f1' : '#1f2937', color: active ? '#fff' : '#9ca3af' }}>{opt}</button>);
        })}</div></div>);
  }
  if (filter.controlType === 'date_range') {
    return (<div><label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <input type="date" value={filters[`${filter.field}_from`] || ''} onChange={e => onChange(`${filter.field}_from`, e.target.value)} style={{ ...inputStyle, flex: 1 }} />
        <input type="date" value={filters[`${filter.field}_to`] || ''} onChange={e => onChange(`${filter.field}_to`, e.target.value)} style={{ ...inputStyle, flex: 1 }} />
      </div></div>);
  }
  return (<div><label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>{label}</label>
    <input value={filters[filter.field] || ''} onChange={e => onChange(filter.field, e.target.value)} style={inputStyle} placeholder={label} /></div>);
};

const ReportChart = ({ chartConfig, chartData, columns }) => {
  const type = chartConfig?.type || 'bar';
  const height = chartConfig?.height || 400;
  const xKey = chartConfig?.xAxis || (columns[0]?.key || 'label');
  const yKey = chartConfig?.yAxis || (columns[1]?.key || 'value');
  if (type === 'pie' || type === 'doughnut') {
    const data = chartData.map(d => ({ name: String(d[xKey] || '?'), value: parseFloat(d[yKey]) || 0 }));
    return (<ResponsiveContainer width="100%" height={height}><PieChart>
      <Pie data={data} cx="50%" cy="50%" innerRadius={type === 'doughnut' ? 70 : 0} outerRadius={150}
        dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
        {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
      </Pie><Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }} />
      {chartConfig?.showLegend && <Legend />}</PieChart></ResponsiveContainer>);
  }
  if (type === 'horizontal_bar') {
    return (<ResponsiveContainer width="100%" height={height}><BarChart data={chartData} layout="vertical" margin={{ top: 5, right: 30, left: 120, bottom: 5 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" /><XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
      <YAxis type="category" dataKey={xKey} tick={{ fill: '#9ca3af', fontSize: 11 }} width={120} />
      <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }} />
      <Bar dataKey={yKey} fill="#6366f1" radius={[0,4,4,0]} /></BarChart></ResponsiveContainer>);
  }
  return (<ResponsiveContainer width="100%" height={height}><BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
    <XAxis dataKey={xKey} tick={{ fill: '#6b7280', fontSize: 11, angle: -30, textAnchor: 'end' }} />
    <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
    <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8 }} />
    {chartConfig?.showLegend && <Legend />}<Bar dataKey={yKey} fill="#6366f1" radius={[4,4,0,0]} /></BarChart></ResponsiveContainer>);
};

const FSMReportRunner = () => {
  const { id } = useParams();
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';

  const [report, setReport] = useState(null);
  const [config, setConfig] = useState(null);
  const [filters, setFilters] = useState({});
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [clients, setClients] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);

  const inputStyle = {
    backgroundColor: '#1a2038', border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: 8, color: '#e0e0e0', padding: '8px 12px',
    fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box',
  };

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`${API}/definitions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
        const def = await res.json();
        if (!res.ok) { setError(def.error); setLoading(false); return; }
        const cfg = typeof def.report_config === 'string' ? JSON.parse(def.report_config) : (def.report_config || {});
        const chartCfg = typeof def.chart_config === 'string' ? JSON.parse(def.chart_config || 'null') : def.chart_config;
        setReport({ ...def, report_config: cfg, chart_config: chartCfg });
        setConfig(JSON.parse(JSON.stringify(cfg)));
        const init = {};
        (cfg.filters || []).forEach(fm => {
          if (!fm.userEditable) return;
          if (fm.controlType === 'date_preset') init[`${fm.field}_preset`] = fm.value || 30;
          else if (fm.controlType === 'multi_select') init[fm.field] = fm.value || [];
          else init[fm.field] = fm.value || '';
        });
        setFilters(init);
      } catch (err) { setError(err.message); }
      setLoading(false);
    };
    load();
  }, [id]);

  const runReport = async () => {
    const token = localStorage.getItem('token');
    setRunning(true); setError(null);
    try {
      const res = await fetch(`${API}/execute`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_definition_id: parseInt(id), report_config: config, filters, page: 1, pageSize: 500 }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); return; }
      setResults(data);
      setSortCol(null); setSortDir('asc');
      setSortCol(null); setSortDir('asc');
      if (report?.chart_config) setViewMode('chart');
    } catch (err) { setError(err.message); }
    finally { setRunning(false); }
  };

  const handleExport = async (format) => {
    const token = localStorage.getItem('token');
    setExporting(format);
    try {
      const response = await fetch(`${API}/execute/export`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', 'Accept-Language': lang },
        body: JSON.stringify({ report_definition_id: parseInt(id), report_config: config, filters, format }),
      });
      if (!response.ok) { const err = await response.json(); alert(err.error || 'Export failed'); return; }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(report?.name || 'report').replace(/[^a-zA-Z0-9_-]/g, '_')}.${format}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) { alert(err.message); }
    finally { setExporting(null); }
  };

  // Load entity lists for global filters
  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      fetch('/api/v1/fsm/clients?limit=500', { headers }).then(r => r.json()).catch(() => ({ clients: [] })),
      fetch('/api/v1/fsm/contracts?limit=500', { headers }).then(r => r.json()).catch(() => ({ contracts: [] })),
      fetch('/api/v1/fsm/technicians?limit=500', { headers }).then(r => r.json()).catch(() => []),
    ]).then(([clRes, ctrRes, techRes]) => {
      setClients(clRes.clients || clRes.data || clRes || []);
      setContracts(ctrRes.contracts || ctrRes.data || ctrRes || []);
      setTechnicians(Array.isArray(techRes) ? techRes : (techRes.agents || techRes.data || []));
    });
  }, []);

  // Auto-run on load
  useEffect(() => { if (config && !results && !running) runReport(); }, [config]);



  if (loading) return (
    <div style={{ background: '#0a0f1e', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#6366f1', fontSize: 16 }}>{isRTL ? 'جاري التحميل...' : 'Loading report...'}</div>
    </div>
  );

  if (!report) return (
    <div style={{ background: '#0a0f1e', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#ef4444', fontSize: 16 }}>{error || 'Report not found'}</div>
    </div>
  );

  const chartConfig = report.chart_config;
  const editableFilters = (config?.filters || []).filter(fm => fm.userEditable);
  const rows = results?.data?.rows || [];
  const columns = results?.data?.columns || [];
  const totalRows = results?.data?.totalRows || 0;
  const execMs = results?.executionMs || 0;
  // Sort rows client-side
  // Sort rows client-side
  const chartData = rows.slice(0, 50);

  // Sort rows client-side (no hook needed)
  const sortedRows = (() => {
    if (!sortCol || !rows.length) return rows;
    return [...rows].sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (va == null) return 1;
      if (vb == null) return -1;
      const na = parseFloat(va), nb = parseFloat(vb);
      if (!isNaN(na) && !isNaN(nb)) return sortDir === 'asc' ? na - nb : nb - na;
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  return (
    <div style={{ background: '#0a0f1e', minHeight: '100vh', direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Top Bar */}
      <div style={{ background: '#111827', borderBottom: '1px solid rgba(255,255,255,0.07)', padding: '14px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 24 }}>{report.icon || '📊'}</span>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f9fafb' }}>
              {isRTL && report.name_ar ? report.name_ar : report.name}
            </h1>
            <p style={{ margin: 0, fontSize: 11, color: '#6b7280' }}>
              {isRTL && report.description_ar ? report.description_ar : report.description}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => setShowBuilder(!showBuilder)} title={isRTL ? 'تخصيص' : 'Customize'}
            style={{ background: showBuilder ? '#1e1b4b' : '#1f2937', border: showBuilder ? '1px solid #6366f1' : '1px solid rgba(255,255,255,0.1)',
              color: showBuilder ? '#a5b4fc' : '#9ca3af', cursor: 'pointer', fontSize: 13, padding: '8px 14px',
              borderRadius: 8, fontWeight: 600, transition: 'all 0.2s' }}>
            ✏️ {isRTL ? 'تخصيص' : 'Customize'}
          </button>
          <button onClick={runReport} disabled={running}
            style={{ background: running ? '#374151' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
              border: 'none', borderRadius: 8, padding: '8px 22px', fontWeight: 700, fontSize: 13, cursor: running ? 'not-allowed' : 'pointer' }}>
            {running ? (isRTL ? '⏳' : '⏳') : '▶'} {running ? (isRTL ? 'جاري...' : 'Running...') : (isRTL ? 'تشغيل' : 'Run')}
          </button>
          <button onClick={() => handleExport('xlsx')} disabled={!!exporting || !results}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12,
              background: '#065f46', color: '#fff', opacity: (!results || exporting) ? 0.5 : 1 }}>
            {exporting === 'xlsx' ? '...' : (isRTL ? '📥 اكسل' : '📥 Excel')}
          </button>
          <button onClick={() => handleExport('pdf')} disabled={!!exporting || !results}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12,
              background: '#7f1d1d', color: '#fff', opacity: (!results || exporting) ? 0.5 : 1 }}>
            {exporting === 'pdf' ? '...' : (isRTL ? '📄 PDF' : '📄 PDF')}
          </button>
        </div>
      </div>

      <div style={{ padding: 24 }}>
        {/* Filters */}
        <div style={{ background: '#111827', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 12, textTransform: 'uppercase' }}>
            {isRTL ? '⚙ الفلاتر' : '⚙ Filters'}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
            {/* Smart global filters based on report datasources */}
            {(() => {
              const ds = config?.datasources || [];
              const primary = ds[0] || '';
              // Map filter fields based on PRIMARY datasource (first in array)
              const clientField = primary === 'work_orders' ? 'work_orders.client_id'
                : primary === 'equipment' ? 'equipment.client_id'
                : primary === 'contracts' ? 'contracts.client_id'
                : primary === 'locations' ? 'locations.client_id'
                : primary === 'time_logs' && ds.includes('work_orders') ? 'work_orders.client_id'
                : primary === 'parts_used' && ds.includes('work_orders') ? 'work_orders.client_id'
                : ds.includes('clients') ? 'clients.id' : null;
              const techField = primary === 'time_logs' ? 'time_logs.agent_id'
                : primary === 'work_orders' ? 'work_orders.assigned_to'
                : primary === 'parts_used' && ds.includes('work_orders') ? 'work_orders.assigned_to'
                : ds.includes('technicians') ? 'technicians.id' : null;
              const contractField = primary === 'work_orders' ? 'work_orders.contract_id'
                : primary === 'contracts' ? 'contracts.id' : null;

              return (<>
                {clientField && (
                  <div>
                    <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>
                      {isRTL ? 'العميل' : 'Client'}
                    </label>
                    <SearchableSelect
                      options={(Array.isArray(clients) ? clients : []).map(c => ({ value: c.id, label: c.name || c.company_name || `Client ${c.id}` }))}
                      value={filters[clientField] || ''}
                      onChange={v => setFilters(prev => ({ ...prev, [clientField]: v }))}
                      placeholder={isRTL ? 'جميع العملاء' : 'All Clients'}
                      isRTL={isRTL} inputStyle={inputStyle} />
                  </div>
                )}
                {contractField && (
                  <div>
                    <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>
                      {isRTL ? 'العقد' : 'Contract'}
                    </label>
                    <SearchableSelect
                      options={(Array.isArray(contracts) ? contracts : []).map(c => ({ value: c.id, label: c.contract_number || c.title || `Contract ${c.id}` }))}
                      value={filters[contractField] || ''}
                      onChange={v => setFilters(prev => ({ ...prev, [contractField]: v }))}
                      placeholder={isRTL ? 'جميع العقود' : 'All Contracts'}
                      isRTL={isRTL} inputStyle={inputStyle} />
                  </div>
                )}
                {techField && (
                  <div>
                    <label style={{ fontSize: 11, color: '#9ca3af', display: 'block', marginBottom: 4 }}>
                      {isRTL ? 'الفني' : 'Technician'}
                    </label>
                    <SearchableSelect
                      options={(Array.isArray(technicians) ? technicians : []).map(t => ({ value: t.agent_id || t.id, label: t.full_name || t.name || `Tech ${t.id}` }))}
                      value={filters[techField] || ''}
                      onChange={v => setFilters(prev => ({ ...prev, [techField]: v }))}
                      placeholder={isRTL ? 'جميع الفنيين' : 'All Technicians'}
                      isRTL={isRTL} inputStyle={inputStyle} />
                  </div>
                )}
              </>);
            })()}
            {/* Report-specific editable filters */}
            {editableFilters.map((filter, idx) => (
              <FilterControl key={idx} filter={filter} filters={filters}
                onChange={(k, v) => setFilters(prev => ({ ...prev, [k]: v }))}
                isRTL={isRTL} inputStyle={inputStyle} />
            ))}
          </div>
        </div>

        {/* Builder Panel */}
        {showBuilder && config && (
          <div style={{ background: '#0d1117', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 14, padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#f9fafb' }}>
                {isRTL ? '✏️ تخصيص التقرير' : '✏️ Customize Report'}
              </span>
              <button onClick={() => {
                const name = prompt(isRTL ? 'اسم التقرير الجديد:' : 'New report name:');
                if (!name) return;
                const token = localStorage.getItem('token');
                fetch(`${API}/definitions/${id}/duplicate`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                  body: JSON.stringify({ name, report_config: config }),
                }).then(r => r.json()).then(d => {
                  if (d.id) alert(isRTL ? `✅ تم الحفظ! ID: ${d.id}` : `✅ Saved! ID: ${d.id}`);
                  else alert(d.error || 'Failed');
                }).catch(e => alert(e.message));
              }}
                style={{ padding: '6px 16px', borderRadius: 8, border: '1px solid #6366f1', background: 'transparent',
                  color: '#6366f1', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                {isRTL ? '💾 حفظ باسم' : '💾 Save As'}
              </button>
            </div>

            {/* Columns currently in report */}
            <div style={{ background: '#111827', borderRadius: 10, padding: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 10, textTransform: 'uppercase' }}>
                {isRTL ? '📋 الأعمدة الحالية (اضغط لإزالة)' : '📋 Current Columns (click to remove)'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(config.columns || []).map((col, idx) => (
                  <button key={idx} type="button" onClick={() => {
                    const newCols = [...config.columns];
                    newCols.splice(idx, 1);
                    setConfig(prev => ({ ...prev, columns: newCols }));
                  }}
                    style={{ padding: '4px 12px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 11,
                      fontWeight: 600, background: '#6366f1', color: '#fff' }}>
                    {isRTL ? (col.alias_ar || col.alias || col.field) : (col.alias || col.field)} ✕
                  </button>
                ))}
                {(!config.columns || config.columns.length === 0) && (
                  <span style={{ fontSize: 11, color: '#4b5563' }}>{isRTL ? 'لا توجد أعمدة' : 'No columns'}</span>
                )}
              </div>
            </div>

            {/* Group By */}
            <div style={{ background: '#111827', borderRadius: 10, padding: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 10, textTransform: 'uppercase' }}>
                {isRTL ? '📊 التجميع الحالي (اضغط لإزالة)' : '📊 Current Grouping (click to remove)'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(config.groupBy || []).map((g, idx) => (
                  <button key={idx} type="button" onClick={() => {
                    const newG = [...config.groupBy];
                    newG.splice(idx, 1);
                    setConfig(prev => ({ ...prev, groupBy: newG }));
                  }}
                    style={{ padding: '4px 12px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 11,
                      fontWeight: 600, background: '#059669', color: '#fff' }}>
                    {g.split('.').pop()} ✕
                  </button>
                ))}
                {(!config.groupBy || config.groupBy.length === 0) && (
                  <span style={{ fontSize: 11, color: '#4b5563' }}>{isRTL ? 'لا يوجد تجميع' : 'No grouping'}</span>
                )}
              </div>
            </div>

            {/* Aggregations */}
            <div style={{ background: '#111827', borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 10, textTransform: 'uppercase' }}>
                {isRTL ? '🔢 الحسابات الحالية (اضغط لإزالة)' : '🔢 Current Aggregations (click to remove)'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(config.aggregations || []).map((a, idx) => (
                  <button key={idx} type="button" onClick={() => {
                    const newA = [...config.aggregations];
                    newA.splice(idx, 1);
                    setConfig(prev => ({ ...prev, aggregations: newA }));
                  }}
                    style={{ padding: '4px 12px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 11,
                      fontWeight: 600, background: '#b45309', color: '#fff' }}>
                    {a.alias || `${a.fn}(${a.field})`} ✕
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div style={{ background: '#450a0a', border: '1px solid #ef4444', borderRadius: 8, padding: 12, color: '#fca5a5', fontSize: 13, marginBottom: 16 }}>
            ❌ {error}
          </div>
        )}

        {results && (
          <>
            {/* Stats + View Toggle */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                {isRTL ? `${totalRows} نتيجة · ${execMs}ms` : `${totalRows} rows · ${execMs}ms`}
              </div>
              {chartConfig && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setViewMode('table')}
                    style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12,
                      background: viewMode === 'table' ? '#6366f1' : '#1f2937', color: '#fff' }}>
                    {isRTL ? '📋 جدول' : '📋 Table'}
                  </button>
                  <button onClick={() => setViewMode('chart')}
                    style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 12,
                      background: viewMode === 'chart' ? '#6366f1' : '#1f2937', color: '#fff' }}>
                    {isRTL ? '📊 رسم' : '📊 Chart'}
                  </button>
                </div>
              )}
            </div>

            {/* Chart */}
            {viewMode === 'chart' && chartConfig && chartData.length > 0 && (
              <div style={{ background: '#111827', borderRadius: 12, padding: 24, marginBottom: 20 }}>
                <ReportChart chartConfig={chartConfig} chartData={chartData} columns={columns} />
              </div>
            )}

            {/* Table */}
            {viewMode === 'table' && (
              <div style={{ overflowX: 'auto', borderRadius: 12, border: '1px solid rgba(255,255,255,0.07)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#1f2937' }}>
                      {columns.map((col, i) => {
                        const colKey = col.key;
                        const isActive = sortCol === colKey;
                        const arrow = isActive ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅';
                        return (
                          <th key={i} onClick={() => {
                              if (sortCol === colKey) {
                                setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
                              } else {
                                setSortCol(colKey);
                                setSortDir('asc');
                              }
                            }}
                            style={{ padding: '12px 16px', textAlign: isRTL ? 'right' : 'left', color: isActive ? '#818cf8' : '#9ca3af',
                              fontWeight: 600, fontSize: 11, textTransform: 'uppercase', whiteSpace: 'nowrap',
                              borderBottom: isActive ? '2px solid #6366f1' : '2px solid #374151',
                              cursor: 'pointer', userSelect: 'none', transition: 'all 0.15s' }}>
                            {isRTL && col.label_ar ? col.label_ar : (col.label || col.key)}
                            <span style={{ fontSize: 9, opacity: isActive ? 1 : 0.4, marginInlineStart: 4 }}>{arrow}</span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row, ri) => (
                      <tr key={ri} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)',
                        background: ri % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)' }}>
                        {columns.map((col, ci) => {
                          const val = row[col.key];
                          const isWO = (col.key === 'WO #' || col.key === 'WO' || col.field === 'wo_number' || (col.label || '').includes('WO'));
                          const woId = isWO && val ? row['wo_id'] || row['id'] : null;
                          // Try to find WO ID from wo_number pattern
                          const woNum = isWO ? String(val || '') : '';
                          return (
                            <td key={ci} style={{ padding: '10px 16px', color: val == null ? '#4b5563' : '#e0e0e0',
                              textAlign: col.alignment === 'right' ? 'right' : 'left' }}>
                              {val == null ? '—' : isWO && val ? (
                                <span style={{ color: '#818cf8', fontWeight: 600, cursor: 'pointer' }}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    const token = localStorage.getItem('token');
                                    try {
                                      const r = await fetch(`/api/v1/fsm/work-orders?search=${encodeURIComponent(val)}&limit=1`, {
                                        headers: { Authorization: `Bearer ${token}` }
                                      });
                                      const d = await r.json();
                                      const wos = d.workOrders || d.data || d || [];
                                      if (wos.length > 0) {
                                        window.open(`/fsm/work-orders/${wos[0].id}`, '_blank');
                                      } else {
                                        window.open(`/fsm/work-orders?search=${encodeURIComponent(val)}`, '_blank');
                                      }
                                    } catch {
                                      window.open(`/fsm/work-orders?search=${encodeURIComponent(val)}`, '_blank');
                                    }
                                  }}
                                  onMouseEnter={e => e.target.style.textDecoration = 'underline'}
                                  onMouseLeave={e => e.target.style.textDecoration = 'none'}>
                                  {String(val)}
                                </span>
                              ) : String(val)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {sortedRows.length === 0 && (
                      <tr><td colSpan={columns.length || 1} style={{ textAlign: 'center', padding: 60, color: '#4b5563', fontSize: 14 }}>
                        {isRTL ? 'لا توجد نتائج' : 'No results'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FSMReportRunner;
